import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLandingContent } from '@/mocks/landingData';

type SolutionCard = {
  id: number;
  title: string;
  description: string;
  image: string;
};

function SolutionCardComponent({ card, index }: { card: SolutionCard; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`group bg-white rounded-lg border border-gray-100 overflow-hidden shadow-soft hover:shadow-card-hover transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="aspect-[5/3] overflow-hidden bg-gray-100">
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-6 md:p-7">
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          {card.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          {card.description}
        </p>
      </div>
    </div>
  );
}

export default function SolutionsSection() {
  const { i18n } = useTranslation();
  const { solutions } = getLandingContent(i18n.language);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="solutions" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div
          ref={sectionRef}
          className={`text-center mb-14 md:mb-20 transition-all duration-700 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-red-600 text-sm font-medium tracking-widest uppercase mb-4">
            {solutions.eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {solutions.title}
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {solutions.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-14 md:mb-20">
          {solutions.cards.map((card, index) => (
            <SolutionCardComponent key={card.id} card={card} index={index} />
          ))}
        </div>

        <div
          className={`text-center transition-all duration-700 delay-500 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-base text-gray-500 mb-5 max-w-xl mx-auto">
            {solutions.cta.text}
          </p>
          <a
            href="#contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white text-sm font-medium rounded-md hover:bg-accent-hover transition-colors duration-300 whitespace-nowrap"
          >
            {solutions.cta.button}
          </a>
        </div>
      </div>
    </section>
  );
}
